import * as Yup from "yup";
import { useWizard } from "components/elements/Wizard/useWizard";
import { useSpeckleProjects } from "components/modules/UploadDialog/lib/hooks/useSpeckleProjects";
import { nameof } from "lib/actions/util";
import {
  Formik,
  Form,
  Field,
  ErrorMessage,
  useFormikContext,
  FormikHelpers,
} from "formik";
import { SpeckleProjectModel } from "../../types/SpeckleProjectModel";
import { SpeckleImportModel } from "../../types/SpeckleImportModel";
import FormInput from "components/elements/Forms/FormInput";
import FormError from "components/elements/Forms/FormError";
import { popToastError } from "lib/actions/toast";

export default function SpeckleCreateProjectForm(p: {
  onCreated(created: SpeckleProjectModel): void;
}) {
  const { stepViewVariant } = useWizard();
  const { values } = useFormikContext<SpeckleImportModel>();
  const speckleConnectedAccountId = parseInt(values.connectedAccountId);
  const { create: createSpeckleProject } = useSpeckleProjects(
    speckleConnectedAccountId,
    values.workspaceId,
  );

  function onSubmitCreateProject(
    projectValues: FormValues,
    form: FormikHelpers<FormValues>,
  ) {
    createSpeckleProject(projectValues)
      .then((projectResponse) => {
        if (!projectResponse.id || !projectResponse.name) {
          popToastError("Response did not contain expected id and name.");
          return;
        }
        p.onCreated(projectResponse);
      })
      .finally(() => {
        form.setSubmitting(false);
      });
  }

  function onCancel() {
    stepViewVariant(null);
  }

  return (
    <Formik
      initialValues={initialFormValues}
      onSubmit={onSubmitCreateProject}
      validationSchema={validation}
      validateOnChange
      validateOnBlur
    >
      {({ values, isSubmitting, handleSubmit, submitForm }) => (
        <Form className="text-sm" onSubmit={handleSubmit}>
          <div className="mt-4 space-y-2">
            <label htmlFor="field_name">Name</label>
            <Field
              id="field_name"
              name={nameof(values, "name")}
              as={FormInput}
              className="bg-white"
              autoComplete="off"
              disabled={isSubmitting}
            />
            <ErrorMessage name={nameof(values, "name")} component={FormError} />
          </div>
          <div className="mt-4 space-y-2">
            <label htmlFor="field_description">Description</label>
            <Field
              id="field_description"
              name={nameof(values, "description")}
              as={FormInput}
              className="bg-white"
              autoComplete="off"
              disabled={isSubmitting}
            />
            <ErrorMessage
              name={nameof(values, "description")}
              component={FormError}
            />
          </div>
          <div className="mt-6 flex flex-row-reverse space-x-2 space-x-reverse">
            <button
              type="submit"
              className="rounded bg-black p-2 px-6 text-white disabled:bg-gray-300"
              onClick={(ev) => (ev.preventDefault(), submitForm())}
              disabled={isSubmitting}
            >
              {!isSubmitting ? "Create" : "Working..."}
            </button>
            <button
              type="reset"
              className="rounded p-2 px-6 disabled:text-gray-300"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
}

type FormValues = Omit<SpeckleProjectModel, "id">;

const initialFormValues: FormValues = {
  name: "",
  description: "",
};

const validation = Yup.object().shape({
  name: Yup.string().nullable().required("Required."),
  description: Yup.string().nullable(),
});
